<template>
    <div id="opportunity-form" class="salesforce-opportunity-form">
        <DxValidationGroup>
            <!-- Toolbar -->
            <div class="form-toolbar">
                <ToolbarForm :is-editing="isEditing" :is-disabled="isLocked" :allow-edit="allowEdit"
                    @edit-canceled="cancelEdit()" @edit-toggled="startEdit()" @save-clicked="handleSaveClick" />
            </div>

            <DxLoadPanel :show-pane="false" width="auto" container="#opportunity-form"
                :position="{ of: '#opportunity-form' }" :visible="isLoading" />

            <!-- Card-Based Layout -->
            <div class="form-cards-container">

                <!-- Informații Generale -->
                <div class="info-card" :class="{ 'collapsed': !expandedSections.general }">
                    <div class="card-section-header" @click="toggleSection('general')">
                        <i class="dx-icon-info"></i>
                        <h3>Informații Generale</h3>
                        <i class="expand-icon"
                            :class="expandedSections.general ? 'dx-icon-chevrondown' : 'dx-icon-chevronright'"></i>
                    </div>
                    <div class="card-grid" v-show="expandedSections.general">
                        <div class="field-group full-width ">
                            <label class="field-label">Observații</label>
                            <DxTextArea v-model:value="opportunityData.Note" :read-only="!isEditing" :height="100" />
                        </div>
                    </div>
                </div>

                <!-- Detalii Financiare -->
                <div class="info-card" :class="{ 'collapsed': !expandedSections.financial }">
                    <div class="card-section-header" @click="toggleSection('financial')">
                        <i class="dx-icon-money"></i>
                        <h3>Achiziție</h3>
                        <i class="expand-icon"
                            :class="expandedSections.financial ? 'dx-icon-chevrondown' : 'dx-icon-chevronright'"></i>
                    </div>
                    <div class="card-grid" v-show="expandedSections.financial">

                        <div class="field-group">
                            <label class="field-label">Buget Estimat</label>
                            <DxNumberBox v-model="opportunityData.BugetEstimat" :read-only="!isEditing"
                                :format="budgetFormat" :showSpinButtons="false" width="100%" />
                        </div>
                        <div class="field-group">
                            <label class="field-label">Valută</label>
                            <DxDropDownBox v-model:opened="isValutaGridBoxOpened" v-model="opportunityData.ValutaID"
                                :data-source="dsValute" v-model:value="sValuta" :show-clear-button="true"
                                display-expr="Simbol" value-expr="Id" :read-only="!isEditing">
                                <template #content="{ data }">
                                    <DxDataGrid :data-source="dsValute" v-model:value="sValuta" :columns="['Simbol']"
                                        :hover-state-enabled="true" height="100%"
                                        @selection-changed="onValueSelectedValuta($event)">
                                        <DxSelection mode="single" />
                                        <DxPaging :enabled="true" :page-size="5" />
                                        <DxFilterRow :visible="true" />
                                        <DxSearchPanel :visible="true" placeholder="Cauta ..." />
                                    </DxDataGrid>
                                </template>
                            </DxDropDownBox>
                        </div>

                        <div class="field-group">
                            <label class="field-label ">Interval Cumparare</label>
                            <DxSelectBox v-model="opportunityData.IntervalCumparare" :data-source="intervalList"
                                display-expr="name" value-expr="name" :read-only="!isEditing" :show-clear-button="true"
                                :search-enabled="true" placeholder="Necunoscut" />
                        </div>
                        <div class="field-group">
                            <label class="field-label">Data Închidere Estimat</label>
                            <DxDateBox v-model="opportunityData.DataInchidereEstimat" type="date"
                                display-format="dd.MM.yyyy" :read-only="!isEditing" />
                        </div>

                        <div class="field-group full-width">
                            <label class="field-label">Campanie</label>
                            <DxSelectBox v-model="opportunityData.CampanieID" :data-source="campanii"
                                display-expr="Denumire" value-expr="ID" :search-enabled="true"
                                :read-only="!isEditing" />
                        </div>



                    </div>
                </div>

            </div>
        </DxValidationGroup>
    </div>
</template>

<script lang="ts">
import { DxTextArea } from 'devextreme-vue/text-area';
import { defineComponent } from 'vue';
import notify from 'devextreme/ui/notify';
import { DxButton } from 'devextreme-vue/button';
import DxLookup from "devextreme-vue/lookup"
import {
    DxForm,
    DxItem as DxFormItem,
    DxGroupItem as DxFormGroupItem,
    DxColCountByScreen,
} from 'devextreme-vue/form';
import { DxNumberBox } from 'devextreme-vue/number-box';
import { DxValidator } from 'devextreme-vue/validator';
import { DxValidationGroup } from 'devextreme-vue/validation-group';
import DxLoadPanel from 'devextreme-vue/load-panel';
import ToolbarForm from '@/components/utils/toolbar-form.vue';
import ODataStore from 'devextreme/data/odata/store';
import AppSetting from '../../settings/appSettings'
import FormTextbox from '../utils/form-textbox.vue';
import DxSelectBox from "devextreme-vue/select-box"
import DxDateBox from "devextreme-vue/date-box"
import DataSource from "devextreme/data/data_source";
import DxDropDownBox from "devextreme-vue/drop-down-box";
import {
    DxDataGrid,
    DxPaging,
    DxSelection,
    DxSearchPanel,
    DxFilterRow,
} from 'devextreme-vue/data-grid';
import CrmSales from '@/services/Comercial/CrmSales';

export default defineComponent({
    components: {
        DxForm,
        DxFormItem,
        DxFormGroupItem,
        DxColCountByScreen,
        DxNumberBox,
        DxValidator,
        DxValidationGroup,
        DxLoadPanel,
        FormTextbox,
        ToolbarForm,
        DxButton,
        DxSelectBox,
        DxLookup,
        DxDateBox,
        DxDropDownBox,
        DxDataGrid,
        DxPaging,
        DxSelection,
        DxSearchPanel,
        DxFilterRow,
        DxTextArea,
    },
    props: {
        opportunityData: {
            type: Object,
            required: true
        },
        isEditingProp: {
            type: Boolean,
            default: false
        },
        isLocked: {
            type: Boolean,
            default: false
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        campanii: {
            type: Array,
            required: true
        },
        allowEdit: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            intervalList: [
                { name: 'Necunoscut' },
                { name: 'Imediat' },
                { name: 'Luna aceasta' },
                { name: 'Anul acesta' },
                { name: 'Trimestrul acesta' },
                { name: 'Trimestrul următor' }

            ],
            dsSales: null as any,
            isEditing: false,
            expandedSections: {
                general: true,
                financial: false
            },
            isValutaGridBoxOpened: false,
            sValuta: null,
            dsValute: null as any,
            isLoading: false,
        }
    },
    computed: {
        budgetFormat(): string {
            const valutaId = this.opportunityData?.ValutaID;
            if (!valutaId) return "#,##0.##";
        }
    },
    watch: {
        'opportunityData.IntervalCumparare': {
            handler(newVal) {
                if (!newVal || newVal === 'Necunoscut') return;
                const now = new Date();
                let newDate = null;

                switch (newVal) {
                    case 'Imediat':
                        // Current date + 2 weeks
                        newDate = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
                        break;
                    case 'Luna aceasta':
                        // Last day of current month
                        newDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                        break;
                    case 'Anul acesta':
                        // Last day of current year
                        newDate = new Date(now.getFullYear(), 11, 31);
                        break;
                    case 'Trimestrul acesta': {
                        // Last day of current quarter
                        const currentMonth = now.getMonth(); // 0-11
                        const quarterEndMonth = Math.floor(currentMonth / 3) * 3 + 2;
                        newDate = new Date(now.getFullYear(), quarterEndMonth + 1, 0);
                        break;
                    }
                    case 'Trimestrul următor': {
                        // Last day of next quarter
                        const currentMonth = now.getMonth();
                        const currentQuarter = Math.floor(currentMonth / 3);
                        const nextQuarter = currentQuarter + 1;
                        let targetYear = now.getFullYear();
                        let targetQuarter = nextQuarter;

                        if (nextQuarter > 3) {
                            targetQuarter = 0;
                            targetYear++;
                        }
                        const quarterEndMonth = targetQuarter * 3 + 2;
                        newDate = new Date(targetYear, quarterEndMonth + 1, 0);
                        break;
                    }
                }

                if (newDate) {
                    this.opportunityData.DataInchidereEstimat = newDate.toISOString();
                }
            }
        },
    },
    methods: {
        createODataContext() {
            this.dsSales = new ODataStore({
                url: AppSetting.API_Url_ComercialOdata + 'CRM_Sales',
                key: 'ID',
                keyType: "Guid",
                version: 4,
                beforeSend: (e) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt"),
                    };
                }
            });

            this.dsValute = new DataSource({
                store: {
                    type: 'odata',
                    url: AppSetting.API_Url_ComercialOdata + 'ERP_Valute',
                    key: 'Id',
                    version: 4,
                    beforeSend: (e: any) => {
                        e.headers = {
                            "Content-Type": "application/json",
                            "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                        };
                    }
                },
                select: ['Id', 'Simbol'],
            });
        },
        onValueSelectedValuta(e) {
            this.isValutaGridBoxOpened = false;
            // Check if opportunityData exists
            if (!this.opportunityData) return;

            if (e.selectedRowKeys.length > 0) {
                this.sValuta = e.selectedRowKeys[0];
                // Sincronizează și obiectul expandat — cardurile citesc opportunityData.Valuta.Simbol,
                // nu doar ValutaID, altfel simbolul valutei rămâne cel vechi până la refresh.
                this.opportunityData.Valuta = e.selectedRowsData?.[0] ?? null;
            } else {
                this.sValuta = null;
                this.opportunityData.Valuta = null;
            }
            this.opportunityData.ValutaID = this.sValuta;
        },
        toggleSection(section: string) {
            this.expandedSections[section] = !this.expandedSections[section];
        },
        startEdit() {
            if (this.isLocked) {
                notify('Această oportunitate este finalizată și nu mai poate fi editată.', 'warning', 2000);
                return;
            }
            this.isEditing = true;
        },
        cancelEdit() {
            this.isEditing = false;
        },
        handleSaveClick({ validationGroup }) {
            const result = validationGroup.validate();
            if (!result.isValid) {
                console.warn('Formular invalid:', result);
                return;
            }
            this.isLoading = true;
            if (!this.opportunityData.ID) {
                console.warn('Lipsește ID-ul oportunității.');
                this.isLoading = false;
                return;
            }
            console.log('Salvez:', this.opportunityData);
            this.dsSales
                .update(this.opportunityData.ID, this.opportunityData)
                .then(() => {
                    console.log('Salvare reușită!');
                    this.$emit('saved', this.opportunityData);
                    this.isEditing = false;
                    this.isLoading = false;
                })
                .catch((err) => {
                    console.error('Eroare la salvare:', err);
                    this.isLoading = false;
                });
        },
    },
    mounted() {
        this.createODataContext();
    },
});
</script>

<style scoped lang="scss">
.salesforce-opportunity-form {
    position: relative;
}

.form-toolbar {
    margin-bottom: 1.5rem;
    display: flex;
    justify-content: flex-end;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e5e7eb;
}

.form-cards-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

/* Info Card Styling */
.info-card {
    background: white;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.card-section-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    background: linear-gradient(to bottom, #fafafa 0%, #f5f5f5 100%);
    border-bottom: 1px solid #e5e7eb;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s ease;

    &:hover {
        background: linear-gradient(to bottom, #f5f5f5 0%, #efefef 100%);
    }

    i {
        font-size: 18px;
        color: #6b7280;
    }

    h3 {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
        color: #111827;
        letter-spacing: -0.01em;
        flex: 1;
    }

    .expand-icon {
        font-size: 14px;
        color: #9ca3af;
        transition: transform 0.2s ease;
        margin-left: auto;
    }
}

.info-card.collapsed {
    .card-section-header {
        border-bottom: none;
    }
}

.card-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
    padding: 1.5rem;
    transition: all 0.3s ease;
}


.field-group {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;

    &.full-width {
        grid-column: 1 / -1;
    }
}

.field-label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    margin-bottom: 2px;
}

/* DevExtreme Input Overrides */
::v-deep(.dx-texteditor) {
    border-radius: 4px;
    border-color: #d1d5db;
    background: white;

    &.dx-state-readonly {
        background: #f9fafb;
        border-color: #e5e7eb;
    }

    &.dx-state-focused {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &.dx-state-hover:not(.dx-state-readonly) {
        border-color: #9ca3af;
    }
}

::v-deep(.dx-texteditor-input) {
    padding: 8px 12px;
    font-size: 14px;
    color: #111827;
    min-height: 38px;
}

::v-deep(.dx-dropdowneditor-button) {
    border-left: 1px solid #e5e7eb;
}

/* Responsive */
@media (max-width: 768px) {
    .card-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
        padding: 1rem;
    }

    .field-group.full-width {
        grid-column: 1;
    }
}
</style>
